import { FC, useCallback, useMemo } from 'react';

import { findRootSvgElement } from '@adflow/ad';
import type { AdElement, Animation, NewElementSpec } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box } from '@chakra-ui/react';
import { shallow } from 'zustand/shallow';

import { useEditorCanvasSession } from '../../hooks/useEditorCanvasSession';
import { useElementSize } from '../../hooks/useElementSize';
import { useStore } from '../../hooks/useStore';
import { EditorCanvasControls } from './EditorCanvasControls';
import { EditorCanvasStage } from './EditorCanvasStage';

/** Deterministic size for unit tests and lab fixtures. */
export const EDITOR_CANVAS_TEST_STAGE_SIZE = { width: 520, height: 400 } as const;

export type EditorCanvasProps = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animation?: Animation | null;
  stageSize?: { width: number; height: number };
  fillContainer?: boolean;
  showControls?: boolean;
  stageTestId?: string;
};

export const EditorCanvas: FC<EditorCanvasProps> = ({
  elements,
  resolvedElements,
  animationOverrides = null,
  animation = null,
  stageSize: stageSizeProp,
  fillContainer = false,
  showControls = true,
  stageTestId,
}) => {
  const {
    setElementsSelected,
    selectedElementIds,
    activeTool,
    setActiveTool,
    insertNewElement,
    deleteElements,
    commitElementMove,
    undo,
    redo,
    undoDepth,
    redoDepth,
  } = useStore(
    (state) => ({
      setElementsSelected: state.setElementsSelected,
      selectedElementIds: state.selectedElementIds,
      activeTool: state.activeTool,
      setActiveTool: state.setActiveTool,
      insertNewElement: state.insertNewElement,
      deleteElements: state.deleteElements,
      commitElementMove: state.commitElementMove,
      undo: state.undo,
      redo: state.redo,
      undoDepth: state.undoStack.length,
      redoDepth: state.redoStack.length,
    }),
    shallow,
  );

  const history = useMemo(
    () => ({
      undoDepth,
      redoDepth,
      undo: () => {
        undo();
      },
      redo: () => {
        redo();
      },
      onMoveCommit: commitElementMove,
    }),
    [commitElementMove, redo, redoDepth, undo, undoDepth],
  );

  const onInsertElement = useCallback(
    (spec: NewElementSpec) => {
      // New text opens ready for typing in the sidebar.
      insertNewElement(spec, { pendingText: spec.tool === 'TEXT' });
    },
    [insertNewElement],
  );

  const { ref: stageMeasureRef, size: measuredStageSize } = useElementSize<HTMLDivElement>();
  const measuredReady = measuredStageSize.width > 0 && measuredStageSize.height > 0;
  const stageSize =
    stageSizeProp ??
    (fillContainer
      ? measuredReady
        ? measuredStageSize
        : { width: 0, height: 0 }
      : EDITOR_CANVAS_TEST_STAGE_SIZE);

  const root = findRootSvgElement(resolvedElements);
  const frame = {
    width: root?.size.width ?? 300,
    height: root?.size.height ?? 250,
  };

  const session = useEditorCanvasSession({
    elements,
    resolvedElements,
    animation,
    frame,
    stageSize,
    selectedElementIds,
    onSelectionChange: setElementsSelected,
    history,
    activeTool,
    onActiveToolChange: setActiveTool,
    onInsertElement,
    onDeleteElements: deleteElements,
  });

  return (
    <Box
      position="relative"
      flex={fillContainer ? 1 : undefined}
      minH={fillContainer ? 0 : undefined}
      width={fillContainer ? '100%' : undefined}
      height={fillContainer ? '100%' : undefined}
      data-test-id="editor-canvas"
    >
      {fillContainer ? (
        <Box ref={stageMeasureRef} position="absolute" inset={0} display="flex">
          {measuredReady ? (
            <EditorCanvasStage
              session={session}
              frame={frame}
              stageSize={stageSize}
              animationOverrides={animationOverrides}
              testId={stageTestId}
              flex={1}
            />
          ) : null}
        </Box>
      ) : (
        <EditorCanvasStage
          session={session}
          frame={frame}
          stageSize={stageSize}
          animationOverrides={animationOverrides}
          testId={stageTestId}
        />
      )}
      {showControls ? <EditorCanvasControls session={session} /> : null}
    </Box>
  );
};

EditorCanvas.displayName = 'EditorCanvas';

export { EditorCanvasStage } from './EditorCanvasStage';
export { EditorCanvasControls } from './EditorCanvasControls';
